import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
    decodeColumns,
    decodeFilterState,
    encodeColumns,
    encodeFilterState,
    type PlanIndexFilterState,
} from 'utils/planIndexFilterParams';

import type { PromoLibraryConfig } from '../../../config/promoLibraryConfig';
import { isPromoLibraryKpiColumn } from '../../../config/promoLibraryConfig';
import type { SortOrder } from '../../types';

/**
 * Written when the user turns every KPI off. Dropping the param instead would
 * read back as "unset" on the next render and restore the config defaults.
 */
const NO_KPIS_SENTINEL = 'none';

export interface PromoLibraryUrlState extends PlanIndexFilterState {
    searchKeyword: string;
    setSearchKeyword: (value: string) => void;
    showChange: boolean;
    setShowChange: (value: boolean) => void;
    /** Keys of the KPI columns the user has switched on. */
    selectedKpis: string[];
    setSelectedKpis: (value: string[]) => void;
    sortBy: string;
    sortOrder: SortOrder;
    setSort: (sortBy: string, sortOrder: SortOrder) => void;
    setAppliedFilters: (result: PlanIndexFilterState) => void;
    resetFilters: () => void;
}

/**
 * Backs all Promo Library view state (search, change toggle, KPI selection, sort,
 * filters) with the URL query string so a filtered view survives refresh,
 * back/forward and can be shared as a link.
 *
 * react-router-dom is ^6.3.0, so the functional `setSearchParams` updater is not
 * available: each setter clones the current params, mutates one key, and writes
 * with `{ replace: true }` so individual tweaks do not stack history entries.
 *
 * The `planIndexFilterParams` codecs are generic over any NewGlobalFilter state
 * despite their name, so the Plan Index and this page share one URL format.
 */
function usePromoLibraryUrlState(config: PromoLibraryConfig | null): PromoLibraryUrlState {
    const [searchParams, setSearchParams] = useSearchParams();

    const kpiColumns = useMemo(() => (config?.columns ?? []).filter(isPromoLibraryKpiColumn), [config]);

    const defaultVisibleKpis = useMemo(
        () => kpiColumns.filter((column) => column.defaultVisible !== false).map((column) => column.key),
        [kpiColumns],
    );

    const filterKeys = useMemo(() => (config?.filterKeys ?? []).map((filter) => filter.value), [config]);

    // The backend's `default_sort_key`, falling back to the leading column so the
    // grid always sends a `sort_by` the endpoint accepts.
    const defaultSortKey = config?.defaultSortKey ?? config?.columns[0]?.key ?? '';

    const searchKeyword = searchParams.get('q') ?? '';
    const showChange = searchParams.get('change') === '1';
    const sortBy = searchParams.get('sortBy') ?? defaultSortKey;
    const sortOrder: SortOrder = searchParams.get('sortOrder') === 'desc' ? 'desc' : 'asc';

    const kpisRaw = searchParams.get('kpis');
    const selectedKpis = useMemo(
        () => (kpisRaw === NO_KPIS_SENTINEL ? [] : decodeColumns(kpisRaw, defaultVisibleKpis)),
        [kpisRaw, defaultVisibleKpis],
    );

    const filtersRaw = searchParams.get('filters');
    const { allCheckedAttribute, allSelectedValueForAllAttribute, applyFilterObject } = useMemo(
        () => decodeFilterState(filtersRaw, filterKeys),
        [filtersRaw, filterKeys],
    );

    const updateParams = useCallback(
        (mutate: (params: URLSearchParams) => void) => {
            const next = new URLSearchParams(searchParams);
            mutate(next);
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const setSearchKeyword = useCallback(
        (value: string) =>
            updateParams((params) => {
                if (value) params.set('q', value);
                else params.delete('q');
            }),
        [updateParams],
    );

    const setShowChange = useCallback(
        (value: boolean) =>
            updateParams((params) => {
                if (value) params.set('change', '1');
                else params.delete('change');
            }),
        [updateParams],
    );

    const setSelectedKpis = useCallback(
        (value: string[]) =>
            updateParams((params) => {
                const encoded = encodeColumns(value, defaultVisibleKpis);
                if (encoded) params.set('kpis', encoded);
                else if (value.length === 0) params.set('kpis', NO_KPIS_SENTINEL);
                else params.delete('kpis');
            }),
        [updateParams, defaultVisibleKpis],
    );

    const setSort = useCallback(
        (nextSortBy: string, nextSortOrder: SortOrder) =>
            updateParams((params) => {
                if (nextSortBy && nextSortBy !== defaultSortKey) params.set('sortBy', nextSortBy);
                else params.delete('sortBy');
                if (nextSortOrder === 'desc') params.set('sortOrder', 'desc');
                else params.delete('sortOrder');
            }),
        [updateParams, defaultSortKey],
    );

    const setAppliedFilters = useCallback(
        (result: PlanIndexFilterState) =>
            updateParams((params) => {
                const encoded = encodeFilterState(result);
                if (encoded) params.set('filters', encoded);
                else params.delete('filters');
            }),
        [updateParams],
    );

    const resetFilters = useCallback(
        () =>
            updateParams((params) => {
                params.delete('filters');
            }),
        [updateParams],
    );

    return {
        searchKeyword,
        setSearchKeyword,
        showChange,
        setShowChange,
        selectedKpis,
        setSelectedKpis,
        sortBy,
        sortOrder,
        setSort,
        allCheckedAttribute,
        allSelectedValueForAllAttribute,
        applyFilterObject,
        setAppliedFilters,
        resetFilters,
    };
}

export default usePromoLibraryUrlState;
